import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { compact, isEqual } from 'lodash'
import { encodeFilters, type FilterObject, type FilterableColumn } from 'app/filter.utils'

// Mirrors defaultFilterTypes' array-value sort in use-table-filters.ts. When the
// user opens and applies the filter panel without changing anything, that sorted
// order is what ends up encoded into the URL, so the comparison here has to sort
// the same way or a column with an out-of-order defaultValue array would never
// match its own defaults.
const sortStrings = (value: string[]): string[] => value.slice().sort((a, b) => (a > b ? 1 : b > a ? -1 : 0))

const sortValue = (value?: string | string[]): string | string[] | undefined =>
  Array.isArray(value) ? sortStrings(value) : value

/**
 * Whether one standalone/loader-filter column's own URL param has moved away
 * from that column's declared default. A column can declare `defaultFilter`
 * AND `customSearchParam` at once (e.g. `channel.keyword` in
 * DashboardHeader.tsx), in which case its default value can legitimately live
 * in its own URL param rather than being absent, so presence alone is not
 * "narrowed" for these columns the way it is for a column with no default.
 *
 * Value decoding mirrors filtersFromURL's `compact(searchParams.get(param).split(','))`
 * in use-table-filters.ts. The operator check mirrors applyFilters there: it
 * only ever writes `${param}Op` when the operator differs from the column's
 * default, clearing it back to `` when it matches, so a present, non-stale
 * `${param}Op` value is itself proof of narrowing regardless of whether the
 * value param happens to still equal the default. The literal string
 * "undefined" is ignored the same way use-table-filters.ts ignores it (a
 * stale value from before that write-side fix).
 */
function isColumnNarrowed(column: FilterableColumn, searchParams: URLSearchParams): boolean {
  const param = column.customSearchParam
  if (!param) return false
  const rawValue = searchParams.get(param)
  // Absent (never written, e.g. an untouched page load) and empty (the
  // post-Clear form: clearFilters writes `param=`, and useHandleSearchParams
  // keeps an empty string rather than deleting the param) both mean "nothing
  // set here", which reads as "at default" whether or not the column
  // declares one.
  if (!rawValue) return false
  if (!column.defaultFilter) return true

  const decodedValue = sortStrings(compact(rawValue.split(`,`)))
  const declaredDefault = sortStrings(
    compact(Array.isArray(column.defaultValue) ? column.defaultValue : [column.defaultValue ?? ``]),
  )
  if (!isEqual(decodedValue, declaredDefault)) return true

  const opParam = searchParams.get(`${param}Op`)
  return !!opParam && opParam !== `undefined` && opParam !== column.defaultOperator
}

/**
 * Whether the user has narrowed this table themselves, as opposed to seeing the
 * defaults the table declares for itself. A table whose columns declare default
 * filters can end up with those defaults encoded into the `filters` URL param
 * (see applyFilters in use-table-filters.ts), so a bare "is the filters param
 * set" check would tell a user who filtered nothing that nothing matched.
 *
 * Columns with `customSearchParam` never contribute to the `filters` param.
 * use-table-filters.ts's applyFilters routes them to their own URL param
 * instead, so they are excluded from the declared-defaults comparison here and
 * checked individually against their own declared default (isColumnNarrowed).
 *
 * Search is not covered here. The two table shells source it differently:
 * TableLayout reads the `query` param, SimpleTable holds its own state.
 */
export function useHasActiveFilters(
  columns?: FilterableColumn[],
  initialFilters?: FilterObject[],
  customSearchParam = `filters`,
): boolean {
  const [searchParams] = useSearchParams()

  const defaultsEncoded = useMemo(() => {
    const defaults: FilterObject[] = initialFilters?.length
      ? initialFilters
      : ((columns ?? [])
          .filter((column) => column.defaultFilter && !column.customSearchParam)
          .map((column) => ({
            attribute: column.attribute,
            displayName: column.displayName,
            operator: column.defaultOperator,
            value: sortValue(column.defaultValue),
            type: column.type,
          })) as FilterObject[])
    return defaults.length ? encodeFilters(defaults) : ``
  }, [columns, initialFilters])

  const encoded = searchParams.get(customSearchParam) ?? ``
  const hasUserFilters = !!encoded && encoded !== defaultsEncoded
  const hasNarrowedStandalone = (columns ?? []).some((column) => isColumnNarrowed(column, searchParams))

  return hasUserFilters || hasNarrowedStandalone
}
